import { useRef, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuLink,
} from "../../components/ui/navigation-menu";
import {
  createTheme,
  getNavLinkClass,
  getNavItemClass,
  getToggleBg,
  getToggleCircle,
  getToggleFocusRing,
  getTitleColor,
  getMenuButtonColor,
} from "../../styles/theme";
import { cn } from "../../lib/utils";
import Divider from "./Divider";
import { useAriaLive } from "../../hooks/useAccessibility";
import { useModeDetection } from "../../hooks/useModeDetection";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { toggleLightMode, selectIsLightMode } from "../../store/slices/appSlice";

interface AppNavigationMenuProps {
  logo: string;
  title: string;
}

function AppNavigationMenu({ logo, title }: AppNavigationMenuProps) {
  const location = useLocation();
  const announce = useAriaLive();
  const { getRoute } = useModeDetection();
  const dispatch = useAppDispatch();
  const isLightMode = useAppSelector(selectIsLightMode);

  const menuRef = useRef<HTMLDivElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Get the appropriate icon based on current route
  const getRouteIcon = () => {
    const path = location.pathname;
    if (path.startsWith("/explorer")) {
      return "/faf - kids icon outline.png";
    } else if (path.startsWith("/admin")) {
      return "/faf - admin icon outline.png";
    } else {
      return "/faf  - adult icon outline.png";
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    }
    // Attach then clean up the listener to the document
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuRef]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    announce(isMenuOpen ? "Navigation menu closed" : "Navigation menu opened");
  };
  const handleToggleLightMode = () => {
    dispatch(toggleLightMode());
    announce(isLightMode ? "Dark mode enabled" : "Light mode enabled");
  };
  const isActive = (path: string) => location.pathname === path;

  // Get theme configuration based on current mode
  const theme = createTheme(isLightMode);

  // Navigation items - updated with new route structure based on current mode
  const navItems = [
    {
      path: getRoute("/home"),
      label: "About",
    },
    {
      path: getRoute("/quiz"),
      label: "Start Quiz",
    },
    {
      path: getRoute("/resources"),
      label: "Resources",
    },
  ];

  // Dark/Light Mode Toggle
  const ThemeToggle = ({ className = "", isMobile = false }) => (
    <div
      className={cn(
        "flex items-center",
        className,
        isMobile ? "justify-center w-full py-4 mt-2" : "mr-4 whitespace-nowrap"
      )}
    >
      <button
        onClick={handleToggleLightMode}
        className={cn(
          "relative inline-flex h-6 w-11 items-center rounded-full",
          "transition-all duration-200 hover:scale-110",
          "focus:outline-none focus:ring-2 focus:ring-offset-2",
          getToggleBg(isLightMode),
          getToggleFocusRing(isLightMode)
        )}
        role="switch"
        aria-checked={isLightMode}
        aria-label={`Toggle theme mode. Currently ${isLightMode ? "light" : "dark"} mode`}
      >
        <span
          className={cn(
            "pointer-events-none block h-5 w-5 rounded-full shadow-lg transition-transform",
            !isLightMode ? "translate-x-5" : "translate-x-0.5",
            getToggleCircle(isLightMode)
          )}
        >
          <span className="flex items-center justify-center h-full w-full text-sm">
            {!isLightMode ? "☀️" : <span className="text-black">☽</span>}
          </span>
        </span>
      </button>
    </div>
  );

  const MenuButton = () => (
    <button
      className={cn(
        "lg:hidden p-3 rounded-md",
        "transition-all duration-200 hover:scale-125",
        "focus:outline-none focus:ring-2 focus:ring-offset-2",
        getMenuButtonColor(isLightMode)
      )}
      onClick={toggleMenu}
      aria-label="Toggle menu"
    >
      <span className="text-5xl transition-transform duration-200">{isMenuOpen ? "✕" : "☰"}</span>
    </button>
  );

  return (
    <nav
      className={`flex flex-col w-full ${theme.colors.bg.primary}`}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="flex justify-between py-2 items-center w-full px-4">
        {/* Left side logo and title */}
        <Link to="/home" className="flex items-center group" aria-label="Find-a-Feeling home page">
          <img
            src={getRouteIcon()}
            className="h-20 ml-1 mr-4 rotate-[15deg] transition-transform duration-200 group-hover:scale-110"
            alt="Find-a-Feeling logo"
          />
          <span
            className={cn(
              "font-bold text-2xl sm:text-4xl whitespace-nowrap font-title",
              getTitleColor(isLightMode)
            )}
          >
            {title}
          </span>
        </Link>
        <Divider
          className="hidden lg:block flex-grow mx-8"
          style={{ marginBottom: "-8px" }}
          isLightMode={isLightMode}
        />

        {/* Right side with theme toggle and navigation */}
        <div className="flex">
          {/* Theme Toggle - visible on large screens only */}
          <ThemeToggle className="hidden lg:flex" />

          {/* Mobile menu button - only shows on small/medium screens */}
          <MenuButton />

          {/* Desktop navigation - shows on large screens only */}
          <NavigationMenu
            className={cn(theme.colors.bg.primary, "hidden lg:flex items-center justify-end")}
          >
            <NavigationMenuList className="list-none flex">
              {navItems.map(item => (
                <NavigationMenuItem
                  key={item.path}
                  className={getNavItemClass(isActive(item.path), false, isLightMode)}
                >
                  <NavigationMenuLink
                    to={item.path}
                    className={getNavLinkClass(isActive(item.path), false, isLightMode)}
                  >
                    <span
                      className={cn(
                        "whitespace-nowrap w-full text-center transition-all duration-200",
                        "group-hover:text-lg",
                        isActive(item.path)
                          ? "group-hover:font-black"
                          : "group-hover:font-extrabold"
                      )}
                    >
                      {item.label}
                    </span>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              ))}
            </NavigationMenuList>
          </NavigationMenu>
        </div>
      </div>

      {/* Mobile navigation - only shows on small/medium screens when menu is open */}
      {isMenuOpen && (
        <div
          ref={menuRef}
          className={cn(
            "lg:hidden border rounded-lg p-4 shadow-lg overflow-hidden",
            "absolute right-4 top-16 p-6",
            "border-border-default",
            isLightMode ? "bg-brand-accent-mint" : "bg-surface-alt"
          )}
          style={{
            minWidth: "280px",
            maxWidth: "calc(100% - 2rem)",
            zIndex: 50,
          }}
          role="menu"
          aria-label="Mobile navigation menu"
        >
          <div className="flex flex-col items-stretch w-full">
            {navItems.map(item => (
              <div
                key={item.path}
                className={getNavItemClass(isActive(item.path), true, isLightMode)}
              >
                <Link
                  to={item.path}
                  className={getNavLinkClass(isActive(item.path), true, isLightMode)}
                  onClick={toggleMenu}
                >
                  <span
                    className={cn(
                      "whitespace-nowrap w-full text-center transition-all duration-200",
                      "group-hover:text-xl",
                      isActive(item.path) ? "group-hover:font-black" : "group-hover:font-extrabold"
                    )}
                  >
                    {item.label}
                  </span>
                </Link>
              </div>
            ))}
            <ThemeToggle isMobile={true} />
          </div>
        </div>
      )}
    </nav>
  );
}

export default AppNavigationMenu;
